
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUserTie,
  faCalendarAlt,
  faChartBar,
  faListAlt,
  faSpinner,
  faExclamationTriangle,
  faArrowRight,
  faClock,
  faTachometerAlt, // For Overview
  faLayerGroup, // For Stages
  faUndo, // For Retry
  faClockRotateLeft, // For overdue
  faExclamationCircle, // For warnings
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
import '../InterviewTracking.css'; // This will be the new themed CSS

// Stage colors consistent with SavedCandidates (using CSS variables is better for theme)
const stageColorsCssVars = {
  'Not Started': 'var(--coreops-status-grey, #adb5bd)',
  'Resume Screening': 'var(--coreops-status-light-blue, #a9def9)',
  'Phone Interview': 'var(--coreops-status-sky-blue, #72d5fd)',
  'Technical Assessment': 'var(--coreops-status-teal, #2ab7ca)',
  'On-site Interview': 'var(--coreops-secondary, #00A4CC)',
  'Reference Check': 'var(--coreops-status-dark-blue, #0077b6)',
  'Offer Extended': 'var(--coreops-status-greenish-teal, #2a9d8f)',
  Hired: 'var(--coreops-success-text, #28a745)',
  Rejected: 'var(--coreops-error-text, #d9534f)',
  'On Hold': 'var(--coreops-status-yellow, #fdc500)',
};

// Stages that should be considered "in process" and can become overdue
const IN_PROCESS_STAGES = [
  'Not Started',
  'Resume Screening',
  'Phone Interview',
  'Technical Assessment',
  'On-site Interview',
  'Reference Check',
  'Offer Extended',
];

function InterviewTracking() {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
      }
      return date.toLocaleDateString('en-US', options);
    } catch (e) {
      return 'Date Error';
    }
  };

  // Enhanced date and time comparison for overdue detection
  const isInterviewOverdue = (candidate) => {
    console.log('🔍 Checking candidate:', candidate.name, {
      nextInterviewDate: candidate.nextInterviewDate,
      interviewStatus: candidate.interviewStatus,
      type: typeof candidate.nextInterviewDate,
    });

    // Check if nextInterviewDate exists
    if (!candidate.nextInterviewDate) {
      console.log('❌ No interview date found');
      return false;
    }

    // Check if candidate is in a stage where they can be overdue
    const isInProcess = IN_PROCESS_STAGES.includes(candidate.interviewStatus);
    if (!isInProcess) {
      console.log('❌ Not in process stage:', candidate.interviewStatus);
      return false;
    }

    try {
      // Parse interview date and time
      let interviewDateTime = new Date(candidate.nextInterviewDate);

      // If invalid, try different parsing methods
      if (isNaN(interviewDateTime.getTime())) {
        const dateStr = candidate.nextInterviewDate.toString();

        // If it's just a date (YYYY-MM-DD), assume 9:00 AM as default interview time
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          interviewDateTime = new Date(dateStr + 'T09:00:00');
        }
        // If it has a T but no timezone, assume local time
        else if (
          dateStr.includes('T') &&
          !dateStr.includes('Z') &&
          !dateStr.includes('+')
        ) {
          interviewDateTime = new Date(dateStr);
        }
        // Try other formats
        else {
          interviewDateTime = new Date(dateStr);
        }
      }

      if (isNaN(interviewDateTime.getTime())) {
        console.log(
          '❌ Invalid date after parsing:',
          candidate.nextInterviewDate
        );
        return false;
      }

      // Get current date and time (now)
      const now = new Date();

      // Compare full datetime (not just date)
      const isOverdue = interviewDateTime < now;

      // Calculate time difference for logging
      const timeDiff = now - interviewDateTime;
      const minutesDiff = Math.floor(timeDiff / (1000 * 60));
      const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

      console.log('⏰ DateTime comparison:', {
        interviewDateTime: interviewDateTime.toLocaleString(),
        currentTime: now.toLocaleString(),
        isOverdue,
        minutesOverdue: minutesDiff > 0 ? minutesDiff : 0,
        hoursOverdue: hoursDiff > 0 ? hoursDiff : 0,
        daysOverdue: daysDiff > 0 ? daysDiff : 0,
        timeDifference: isOverdue
          ? `${daysDiff > 0 ? daysDiff + 'd ' : ''}${hoursDiff % 24 > 0 ? (hoursDiff % 24) + 'h ' : ''
          }${minutesDiff % 60 > 0 ? (minutesDiff % 60) + 'm' : ''}`
          : 'Future interview',
      });

      return isOverdue;
    } catch (error) {
      console.error('❌ Error in overdue check:', error);
      return false;
    }
  };

  // Enhanced overdue calculation with time precision
  const getDetailedOverdue = (dateString) => {
    if (!dateString)
      return { days: 0, hours: 0, minutes: 0, displayText: '0 minutes' };

    try {
      let interviewDateTime = new Date(dateString);

      if (isNaN(interviewDateTime.getTime())) {
        const dateStr = dateString.toString();
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          interviewDateTime = new Date(dateStr + 'T09:00:00');
        } else {
          interviewDateTime = new Date(dateStr);
        }
      }

      if (isNaN(interviewDateTime.getTime()))
        return { days: 0, hours: 0, minutes: 0, displayText: '0 minutes' };

      const now = new Date();
      const diffTime = now - interviewDateTime;

      if (diffTime <= 0)
        return { days: 0, hours: 0, minutes: 0, displayText: '0 minutes' };

      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

      // Create display text
      let displayText = '';
      if (days > 0) {
        displayText = `${days} day${days !== 1 ? 's' : ''}`;
        if (hours > 0)
          displayText += `, ${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (hours > 0) {
        displayText = `${hours} hour${hours !== 1 ? 's' : ''}`;
        if (minutes > 0) displayText += `, ${minutes} min`;
      } else {
        displayText = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }

      return { days, hours, minutes, displayText };
    } catch (error) {
      console.error('Error calculating detailed overdue:', error);
      return { days: 0, hours: 0, minutes: 0, displayText: '0 minutes' };
    }
  };

  // Legacy function for backward compatibility
  const getDaysOverdue = (dateString) => {
    return getDetailedOverdue(dateString).days;
  };

  // Process candidates to separate upcoming and overdue
  const processCandidates = (candidates) => {
    if (!candidates || !Array.isArray(candidates)) {
      console.log('❌ No candidates array provided');
      return { upcoming: [], overdue: [] };
    }

    console.log('🔄 Processing', candidates.length, 'candidates...');

    const upcoming = [];
    const overdue = [];

    candidates.forEach((candidate, index) => {
      console.log(
        `\n--- Processing candidate ${index + 1}: ${candidate.name} ---`
      );

      if (isInterviewOverdue(candidate)) {
        const overdueDetails = getDetailedOverdue(candidate.nextInterviewDate);
        console.log(
          `🔴 OVERDUE: ${candidate.name} is ${overdueDetails.displayText} overdue`
        );

        overdue.push({
          ...candidate,
          daysOverdue: overdueDetails.days,
          overdueDetails: overdueDetails,
          isOverdue: true,
        });
      } else {
        console.log(`🔵 UPCOMING: ${candidate.name}`);
        upcoming.push({
          ...candidate,
          isOverdue: false,
        });
      }
    });

    console.log('\n📊 FINAL RESULTS:');
    console.log(
      `✅ Upcoming: ${upcoming.length}`,
      upcoming.map((c) => c.name)
    );
    console.log(
      `❌ Overdue: ${overdue.length}`,
      overdue.map((c) => `${c.name} (${c.overdueDetails?.displayText})`)
    );

    // Sort overdue by total time overdue (most overdue first)
    overdue.sort((a, b) => {
      const aTotalMinutes =
        (a.overdueDetails?.days || 0) * 24 * 60 +
        (a.overdueDetails?.hours || 0) * 60 +
        (a.overdueDetails?.minutes || 0);
      const bTotalMinutes =
        (b.overdueDetails?.days || 0) * 24 * 60 +
        (b.overdueDetails?.hours || 0) * 60 +
        (b.overdueDetails?.minutes || 0);
      return bTotalMinutes - aTotalMinutes;
    });

    return { upcoming, overdue };
  };

  useEffect(() => {
    fetchTrackingData();
  }, []);

  const fetchTrackingData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${backend_url}/interview-tracking`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to fetch interview tracking data: ${response.status}`
        );
      }
      const data = await response.json();

      // Debug: Log the fetched data
      console.log('📡 Fetched tracking data:', data);

      // TEST MODE: Uncomment below to test with sample data
      // const testData = {
      //   ...data,
      //   upcomingInterviews: [
      //     ...(data.upcomingInterviews || []),
      //     // Add test overdue candidate - 2 days ago
      //     {
      //       _id: 'test-overdue-1',
      //       name: 'Test Overdue (2 days)',
      //       email: 'testoverdue1@example.com',
      //       nextInterviewDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      //       interviewStatus: 'Phone Interview',
      //       updatedAt: new Date().toISOString()
      //     },
      //     // Add test overdue candidate - 3 hours ago
      //     {
      //       _id: 'test-overdue-2',
      //       name: 'Test Overdue (3 hours)',
      //       email: 'testoverdue2@example.com',
      //       nextInterviewDate: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      //       interviewStatus: 'Technical Assessment',
      //       updatedAt: new Date().toISOString()
      //     },
      //     // Add test overdue candidate - 30 minutes ago
      //     {
      //       _id: 'test-overdue-3',
      //       name: 'Test Overdue (30 min)',
      //       email: 'testoverdue3@example.com',
      //       nextInterviewDate: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      //       interviewStatus: 'On-site Interview',
      //       updatedAt: new Date().toISOString()
      //     },
      //     // Add test upcoming candidate - 2 hours from now
      //     {
      //       _id: 'test-upcoming-1',
      //       name: 'Test Upcoming (2 hours)',
      //       email: 'testupcoming1@example.com',
      //       nextInterviewDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      //       interviewStatus: 'Technical Assessment',
      //       updatedAt: new Date().toISOString()
      //     },
      //     // Add test upcoming candidate - tomorrow
      //     {
      //       _id: 'test-upcoming-2',
      //       name: 'Test Upcoming (1 day)',
      //       email: 'testupcoming2@example.com',
      //       nextInterviewDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      //       interviewStatus: 'Phone Interview',
      //       updatedAt: new Date().toISOString()
      //     }
      //   ]
      // };
      // console.log('🧪 Using test data:', testData);
      // setTrackingData(testData);

      setTrackingData(data);
    } catch (err) {
      console.error('❌ Error fetching interview tracking data:', err);
      setError(
        err.message ||
        'Failed to load interview tracking data. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => {
    if (
      !trackingData ||
      !trackingData.upcomingInterviews ||
      !trackingData.recentlyUpdated
    ) {
      return (
        <p className='coreops-no-data-text'>
          Overview data is currently unavailable.
        </p>
      );
    }

    const { upcoming, overdue } = processCandidates(
      trackingData.upcomingInterviews
    );

    return (
      <div className='coreops-overview-layout'>
        {/* Debug Information - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div
            style={{
              background: '#f1f3f4',
              padding: '15px',
              margin: '15px 0',
              border: '2px solid #e8eaed',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            <h4 style={{ margin: '0 0 10px 0', color: '#1a73e8' }}>
              🔧 DEBUG INFORMATION
            </h4>
            <div style={{ marginBottom: '10px' }}>
              <strong>📊 Processing Results:</strong>
              <div>
                • Total candidates:{' '}
                {trackingData.upcomingInterviews?.length || 0}
              </div>
              <div style={{ color: '#d93025' }}>
                • 🔴 Overdue: {overdue.length}
              </div>
              <div style={{ color: '#1a73e8' }}>
                • 🔵 Upcoming: {upcoming.length}
              </div>
              <div>• Current time: {new Date().toLocaleString()}</div>
            </div>

            {overdue.length > 0 && (
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  background: '#fce8e6',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#d93025' }}>
                  🚨 OVERDUE CANDIDATES:
                </strong>
                {overdue.map((c, i) => (
                  <div key={i} style={{ marginLeft: '10px', fontSize: '12px' }}>
                    • {c.name}: {c.nextInterviewDate}
                    <span style={{ color: '#d93025', fontWeight: 'bold' }}>
                      ({c.overdueDetails?.displayText || 'calculating...'}{' '}
                      overdue)
                    </span>
                  </div>
                ))}
              </div>
            )}

            {upcoming.length > 0 && (
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  background: '#e8f0fe',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#1a73e8' }}>
                  📅 UPCOMING CANDIDATES:
                </strong>
                {upcoming.slice(0, 3).map((c, i) => (
                  <div key={i} style={{ marginLeft: '10px', fontSize: '12px' }}>
                    • {c.name}: {c.nextInterviewDate} ({c.interviewStatus})
                  </div>
                ))}
                {upcoming.length > 3 && (
                  <div style={{ marginLeft: '10px', fontSize: '12px' }}>
                    ... and {upcoming.length - 3} more
                  </div>
                )}
              </div>
            )}

            <details style={{ marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer', color: '#1a73e8' }}>
                📋 View Raw Data Sample
              </summary>
              <pre
                style={{
                  fontSize: '11px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  background: 'white',
                  padding: '10px',
                  borderRadius: '4px',
                  marginTop: '5px',
                }}
              >
                {JSON.stringify(
                  trackingData.upcomingInterviews?.slice(0, 2) || [],
                  null,
                  2
                )}
              </pre>
            </details>

            <button
              onClick={() => {
                // Test function to verify datetime logic
                console.log('🧪 TESTING DATETIME LOGIC:');

                const testCases = [
                  {
                    name: 'Test Past Date (2 days ago)',
                    nextInterviewDate: new Date(
                      Date.now() - 2 * 24 * 60 * 60 * 1000
                    ).toISOString(),
                    interviewStatus: 'Phone Interview',
                  },
                  {
                    name: 'Test Past Time (3 hours ago)',
                    nextInterviewDate: new Date(
                      Date.now() - 3 * 60 * 60 * 1000
                    ).toISOString(),
                    interviewStatus: 'Technical Assessment',
                  },
                  {
                    name: 'Test Recent Past (30 minutes ago)',
                    nextInterviewDate: new Date(
                      Date.now() - 30 * 60 * 1000
                    ).toISOString(),
                    interviewStatus: 'On-site Interview',
                  },
                  {
                    name: 'Test Future Time (2 hours from now)',
                    nextInterviewDate: new Date(
                      Date.now() + 2 * 60 * 60 * 1000
                    ).toISOString(),
                    interviewStatus: 'Phone Interview',
                  },
                ];

                testCases.forEach((testCase) => {
                  console.log(`\n--- Testing: ${testCase.name} ---`);
                  const result = isInterviewOverdue(testCase);
                  const details = getDetailedOverdue(
                    testCase.nextInterviewDate
                  );
                  console.log('Is overdue?', result);
                  console.log('Time details:', details);
                });
              }}
              style={{
                marginTop: '10px',
                padding: '5px 10px',
                background: '#1a73e8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              🧪 Test DateTime Logic
            </button>
          </div>
        )}

        {/* Overdue Interviews Section - Always show if there are any overdue */}
        {overdue.length > 0 && (
          <div
            className='coreops-overview-column overdue-section'
            style={{ order: -1 }}
          >
            <h3
              className='coreops-section-title modern overdue'
              style={{ color: '#d9534f' }}
            >
              <FontAwesomeIcon icon={faExclamationCircle} /> 🚨 OVERDUE
              INTERVIEWS
              <span
                style={{
                  backgroundColor: '#d9534f',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  marginLeft: '8px',
                }}
              >
                {overdue.length}
              </span>
            </h3>
            {overdue.map((candidate) => (
              <div
                key={candidate._id}
                className='coreops-tracking-card overdue'
                style={{
                  border: '2px solid #d9534f',
                  backgroundColor: '#fdf2f2',
                  boxShadow: '0 4px 8px rgba(217, 83, 79, 0.2)',
                }}
              >
                <div className='coreops-card-header'>
                  <Link
                    to={`/candidates/${candidate._id}`}
                    className='coreops-candidate-name-link'
                    style={{ color: '#d9534f', fontWeight: 'bold' }}
                  >
                    🔥 {candidate.name}
                  </Link>
                  <span
                    className='coreops-tracking-date overdue'
                    style={{ color: '#d9534f' }}
                  >
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                    {formatDate(candidate.nextInterviewDate)}
                    <span
                      className='days-overdue'
                      style={{
                        backgroundColor: '#d9534f',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        marginLeft: '5px',
                      }}
                    >
                      ⏰{' '}
                      {candidate.overdueDetails?.displayText ||
                        `${candidate.daysOverdue} day${candidate.daysOverdue !== 1 ? 's' : ''
                        }`}{' '}
                      OVERDUE
                    </span>
                  </span>
                </div>
                <div className='coreops-card-body'>
                  <span
                    className='coreops-status-pill small overdue'
                    style={{
                      backgroundColor: '#d9534f',
                      color: 'white',
                      fontWeight: 'bold',
                      border: '2px solid #d9534f',
                    }}
                  >
                    🚨 OVERDUE - {candidate.interviewStatus}
                  </span>
                  <span
                    className='coreops-candidate-email'
                    style={{ color: '#666' }}
                  >
                    {candidate.email}
                  </span>
                </div>
                <div className='coreops-card-footer'>
                  <Link
                    to={`/candidates/${candidate._id}`}
                    className='coreops-profile-link urgent'
                    style={{
                      backgroundColor: '#d9534f',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    🚨 URGENT: Update Status{' '}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming Interviews Section */}
        <div className='coreops-overview-column'>
          <h3
            className='coreops-section-title modern'
            style={{ color: '#00A4CC' }}
          >
            <FontAwesomeIcon icon={faCalendarAlt} /> 📅 UPCOMING INTERVIEWS
            {upcoming.length > 0 && (
              <span
                style={{
                  backgroundColor: '#00A4CC',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  marginLeft: '8px',
                }}
              >
                {upcoming.length}
              </span>
            )}
          </h3>
          {upcoming.length > 0 ? (
            upcoming.map((candidate) => (
              <div
                key={candidate._id}
                className='coreops-tracking-card upcoming'
                style={{
                  border: '2px solid #00A4CC',
                  backgroundColor: '#f8fcff',
                  boxShadow: '0 2px 4px rgba(0, 164, 204, 0.1)',
                }}
              >
                <div className='coreops-card-header'>
                  <Link
                    to={`/candidates/${candidate._id}`}
                    className='coreops-candidate-name-link'
                    style={{ color: '#00A4CC', fontWeight: '600' }}
                  >
                    {candidate.name}
                  </Link>
                  <span
                    className='coreops-tracking-date'
                    style={{ color: '#00A4CC' }}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {formatDate(candidate.nextInterviewDate)}
                  </span>
                </div>
                <div className='coreops-card-body'>
                  <span
                    className='coreops-status-pill small'
                    style={{
                      backgroundColor:
                        stageColorsCssVars[
                        candidate.interviewStatus || 'Not Started'
                        ] || '#00A4CC',
                      color: 'white',
                      fontWeight: '500',
                    }}
                  >
                    📋 {candidate.interviewStatus || 'Not Started'}
                  </span>
                  <span
                    className='coreops-candidate-email'
                    style={{ color: '#666' }}
                  >
                    {candidate.email}
                  </span>
                </div>
                <div className='coreops-card-footer'>
                  <Link
                    to={`/candidates/${candidate._id}`}
                    className='coreops-profile-link'
                    style={{
                      color: '#00A4CC',
                      textDecoration: 'none',
                      fontWeight: '500',
                    }}
                  >
                    View Profile <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <p
              className='coreops-no-data-text'
              style={{
                color: '#666',
                fontStyle: 'italic',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                border: '1px dashed #ddd',
              }}
            >
              ✅ No upcoming interviews scheduled.
            </p>
          )}
        </div>

        {/* Recently Updated Section */}
        <div className='coreops-overview-column'>
          <h3 className='coreops-section-title modern'>
            <FontAwesomeIcon icon={faClock} /> Recently Updated Candidates
          </h3>
          {trackingData.recentlyUpdated.map((candidate) => (
            <div key={candidate._id} className='coreops-tracking-card recent'>
              <div className='coreops-card-header'>
                <Link
                  to={`/candidates/${candidate._id}`}
                  className='coreops-candidate-name-link'
                >
                  {candidate.name}
                </Link>
                <span className='coreops-update-date'>
                  Updated: {formatDate(candidate.updatedAt, false)}
                </span>
              </div>
              <div className='coreops-card-body'>
                <span
                  className='coreops-status-pill small'
                  style={{
                    backgroundColor:
                      stageColorsCssVars[
                      candidate.interviewStatus || 'Not Started'
                      ],
                  }}
                >
                  {candidate.interviewStatus || 'Not Started'}
                </span>
              </div>
              <div className='coreops-card-footer'>
                <Link
                  to={`/candidates/${candidate._id}`}
                  className='coreops-profile-link'
                >
                  View Profile <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCandidatesByStage = () => {
    if (!trackingData || !trackingData.stageCounts) return null;

    return (
      <div>
        <h3 className='coreops-section-title modern'>
          <FontAwesomeIcon icon={faLayerGroup} /> Candidate Distribution by
          Stage
        </h3>
        <div className='coreops-stages-grid'>
          {trackingData.stageCounts.map((stage) => (
            <Link
              to={`/candidates?stage=${encodeURIComponent(stage._id)}`}
              key={stage._id}
              className='coreops-stage-card-link'
            >
              <div
                className='coreops-stage-card'
                style={{
                  borderLeftColor: stageColorsCssVars[stage._id] || '#9e9e9e',
                }}
              >
                <div className='coreops-stage-card-header'>
                  <h4 className='coreops-stage-title'>{stage._id}</h4>
                  <span className='coreops-stage-count'>{stage.count}</span>
                </div>
                <div className='coreops-stage-card-footer'>
                  View Candidates <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className='coreops-page-container interview-tracking-page'>
      <div className='coreops-content-wrapper'>
        <header className='coreops-main-header results-sub-header'>
          <h1>
            <FontAwesomeIcon icon={faChartBar} /> Interview Pipeline Tracking
          </h1>
        </header>

        <nav className='coreops-tabs-navigation'>
          <button
            className={`coreops-tab-button ${activeTab === 'overview' ? 'active' : ''
              }`}
            onClick={() => setActiveTab('overview')}
          >
            <FontAwesomeIcon icon={faTachometerAlt} /> Overview
          </button>
          <button
            className={`coreops-tab-button ${activeTab === 'stages' ? 'active' : ''
              }`}
            onClick={() => setActiveTab('stages')}
          >
            <FontAwesomeIcon icon={faLayerGroup} /> By Stage
          </button>
        </nav>

        <div className='coreops-tab-content'>
          {loading ? (
            <div className='coreops-message-container loading'>
              <FontAwesomeIcon icon={faSpinner} spin size='3x' />
              <p>Loading Tracking Data...</p>
            </div>
          ) : error ? (
            <div className='coreops-message-container error'>
              <FontAwesomeIcon icon={faExclamationTriangle} size='3x' />
              <p className='error-title'>Error Loading Data</p>
              <p>{error}</p>
              <button
                className='coreops-button coreops-button-primary'
                onClick={fetchTrackingData}
              >
                <FontAwesomeIcon icon={faUndo} /> Retry
              </button>
            </div>
          ) : trackingData ? (
            <>
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'stages' && renderCandidatesByStage()}
            </>
          ) : (
            <div className='coreops-message-container empty'>
              <FontAwesomeIcon icon={faChartBar} size='4x' />
              <p className='empty-title'>No Tracking Data</p>
              <p className='empty-subtitle'>
                Interview tracking information could not be loaded or is
                unavailable.
              </p>
            </div>
          )}
        </div>
        <footer className='coreops-footer results-sub-footer'>
          <p>© {new Date().getFullYear()} COREHire - Interview Pipeline</p>
        </footer>
      </div>
    </div>
  );
}

export default InterviewTracking;
